import {
  Box,
  Typography,
  Grid,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Paper,
  Table,
  TableBody,
  Divider,
  Badge,
  Container,
  Pagination,
  FormControl,
  Select,
  MenuItem,
  SelectChangeEvent,
} from "@mui/material";
import { useState, useEffect } from "react";
import { Loading } from "../../components/Loading";
import { financeApi } from "./apiService";
import { usePlanStatus } from "../../hooks/usePlanStatus";
import {
  tableHeaderStyles,
  tableRowStyles,
  commonTypographyProps,
} from "../../utils/styles";
import {
  formatCurrency,
  formatDate,
  formatText,
  getDateRangeForFilter,
  TimeFilterWithNone,
} from "../../utils/utils";
import { PROJECT_TERMINOLOGY } from "../../project/project_module/enums";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import NavigationHeader from "../../components/NavigationHeader";
import PaymentModal from "./components/PaymentModal";
import AddIcon from "@mui/icons-material/Add";
import { DateFilterComponent } from "./IncomeTransaction";

export const getTransactionTypeColor = (transactionType: string) => {
  if (["Credit", "Due", "Payment", "Paid"].includes(transactionType))
    return "success";
  if (["Debit", "Settlement", "Advance", "Expense"].includes(transactionType))
    return "error";
  return "info";
};

export const PaymentModeFormatter = {
  CASH: "Cash",
  ONLINE: "Online",
  FUEL_CARD: "Fuel Card",
  ADVANCE_CASH: "Advance Cash",
  UPI: "UPI",
  CHEQUE: "Cheque",
  BANK_TRANSFER: "Bank Transfer",
};

const Transactions = () => {
  const { partyType, partyId } = useParams();
  const location = useLocation() as any;
  const { party, partyType: originalPartyType } = location.state || {};
  const { planStatus, loading } = usePlanStatus();
  const navigate = useNavigate();
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);

  // Transaction Modal State
  const [selectedParty, setSelectedParty] = useState<any>(party);
  const [transactions, setTransactions] = useState<any[]>([]);
  const [transactionLoading, setTransactionLoading] = useState(false);

  // Date filter state
  const [timeFilter, setTimeFilter] = useState<TimeFilterWithNone>("");
  const [customStartDate, setCustomStartDate] = useState("");
  const [customEndDate, setCustomEndDate] = useState("");

  // Pagination state
  const [pageSize, setPageSize] = useState(20);
  const [pagination, setPagination] = useState<any>({
    totalRecords: 0,
    totalPages: 0,
    currentPage: 1,
    limit: 20,
    hasNextPage: false,
    hasPreviousPage: false,
  });

  const [summary, setSummary] = useState<any>({
    totalDebit: 0,
    totalCredit: 0,
    totalBalance: 0,
    totalCount: 0,
  });

  // Fetch transactions when route params or plan status become available
  useEffect(() => {
    // If route params are missing, reset state
    if (!partyId || !partyType) {
      setTransactions([]);
      return;
    }

    // Wait until plan status is available before fetching
    if (!planStatus?.company?.id || loading) return;

    // Load all transactions by default (as it was before)
    fetchTransactions(partyId, partyType, 1);
  }, [partyId, partyType, planStatus?.company?.id, loading]);

  const fetchTransactions = async (
    partyId: string,
    partyType: string,
    page: number,
    limitOverride?: number,
    applyFilter: boolean = true
  ) => {
    if (!planStatus?.company?.id) return;

    setTransactionLoading(true);

    try {
      // Only apply date filter if user has selected a specific filter
      let startDate = null;
      let endDate = null;

      if (timeFilter && applyFilter) {
        if (timeFilter === "custom" && customStartDate && customEndDate) {
          startDate = customStartDate;
          endDate = customEndDate;
        } else if (timeFilter !== "custom") {
          const dateRange = getDateRangeForFilter(timeFilter);
          // Convert to YYYY-MM-DD format for backend using local timezone
          startDate = dateRange.start.toLocaleDateString("en-CA"); // en-CA gives YYYY-MM-DD format
          endDate = dateRange.end.toLocaleDateString("en-CA");

          // Debug logging
          console.log(`Date filter "${timeFilter}":`, {
            start: dateRange.start.toLocaleDateString("en-IN"), // Indian format for display
            end: dateRange.end.toLocaleDateString("en-IN"),
            startDate, // API format
            endDate, // API format
          });
        }
      }

      // For empty filter, don't send any date parameters to backend

      const response = await financeApi.getTransactionsByPartyId(
        partyType,
        partyId,
        "expense",
        page,
        limitOverride ?? pageSize,
        startDate,
        endDate
      );

      const newTransactions = response.transactions || [];
      const paginationData = response.pagination || {};
      const summary = response.summary;

      setTransactions(newTransactions);
      setSummary(summary);
      setPagination({
        totalRecords: paginationData.totalRecords || 0,
        totalPages: paginationData.totalPages || 1,
        currentPage: paginationData.currentPage || page,
        limit: paginationData.limit || limitOverride || pageSize,
        hasNextPage: Boolean(paginationData.hasNextPage),
        hasPreviousPage: Boolean(paginationData.hasPreviousPage),
      });
      if (paginationData.limit) setPageSize(paginationData.limit);
    } catch (error) {
      console.error("Error fetching transactions:", error);
    } finally {
      setTransactionLoading(false);
    }
  };

  // Handle page change
  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    if (partyId && partyType) {
      fetchTransactions(partyId, partyType, value, pageSize);
    }
  };

  // Handle page size change
  const handlePageSizeChange = (event: SelectChangeEvent<number>) => {
    const newPageSize = event.target.value as number;
    setPageSize(newPageSize);
    if (partyId && partyType) {
      fetchTransactions(partyId, partyType, 1, newPageSize);
    }
  };

  // Handle applying the selected filter
  const handleApplyFilter = () => {
    if (timeFilter && partyId && partyType) {
      fetchTransactions(partyId, partyType, 1, pageSize);
    }
  };

  // Handle clear filters
  const handleClearFilters = () => {
    setTimeFilter("");
    setCustomStartDate("");
    setCustomEndDate("");
    // Load all transactions when clearing filters
    if (partyId && partyType) {
      fetchTransactions(partyId, partyType, 1, pageSize, false);
    }
  };

  if (!partyId || !partyType) return null;

  return (
    <Container maxWidth="lg" sx={{ py: 2 }}>
      <NavigationHeader
        breadcrumbs={[
          { label: "Finance", path: "/finance" },
          { label: "Payables", path: "/finance/expense" },
          {
            label: originalPartyType
              ? formatText(originalPartyType)
              : "Party Details",
            path: `/finance/expense/party/${originalPartyType}`,
          },
          { label: "Transactions" },
        ]}
        title={selectedParty?.name}
        button={{
          label: "Payment",
          onClick: () => {
            setSelectedParty(party);
            setPaymentModalOpen(true);
          },
          icon: <AddIcon />,
        }}
      />

      {/* Date Filter Section */}
      <DateFilterComponent
        timeFilter={timeFilter}
        setTimeFilter={setTimeFilter}
        customStartDate={customStartDate}
        setCustomStartDate={setCustomStartDate}
        customEndDate={customEndDate}
        setCustomEndDate={setCustomEndDate}
        onApplyFilter={handleApplyFilter}
        onClearFilters={handleClearFilters}
      />

      <Paper sx={{ p: 0 }}>
        <Box sx={{ p: 2 }}>
          {/* Party Summary */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={6} sm={2}>
              <Typography variant="h6" sx={commonTypographyProps}>
                Payable
              </Typography>
              <Typography variant="body1" sx={commonTypographyProps}>
                {formatCurrency(summary?.totalDue || 0)}
              </Typography>
            </Grid>
            <Grid item xs={6} sm={2}>
              <Typography variant="h6" sx={commonTypographyProps}>
                Paid
              </Typography>
              <Typography variant="body1" sx={commonTypographyProps}>
                {formatCurrency(summary?.totalPaid || 0)}
              </Typography>
            </Grid>
            <Grid item xs={6} sm={2}>
              <Typography variant="h6" sx={commonTypographyProps}>
                Balance
              </Typography>
              <Typography variant="body1" sx={commonTypographyProps}>
                {formatCurrency(summary?.totalBalance)}
              </Typography>
            </Grid>
          </Grid>

          <Divider sx={{ mb: 2 }} />

          {/* Transactions Table */}
          {transactionLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <Loading message="Loading transactions..." />
            </Box>
          ) : (
            <>
              <TableContainer sx={{ mb: 2 }}>
                <Table
                  size="small"
                  sx={{
                    "& .MuiTableCell-root": {
                      padding: "4px 8px",
                      fontSize: "0.75rem",
                    },
                  }}
                >
                  <TableHead>
                    <TableRow>
                      <TableCell sx={tableHeaderStyles}>Date</TableCell>
                      <TableCell sx={tableHeaderStyles}>
                        Transaction Details
                      </TableCell>
                      <TableCell sx={tableHeaderStyles}>Payment Mode</TableCell>
                      <TableCell sx={tableHeaderStyles}>Type</TableCell>
                      <TableCell sx={tableHeaderStyles}>Amount</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {transactions?.length === 0 &&
                    !transactionLoading &&
                    timeFilter ? (
                      <TableRow>
                        <TableCell
                          colSpan={5}
                          sx={{ textAlign: "center", py: 4 }}
                        >
                          <Typography variant="body2" color="text.secondary">
                            No transactions found for the selected date range.
                            Try adjusting your filter or clear filters to view
                            all transactions.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : transactions?.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={5}
                          sx={{ textAlign: "center", py: 4 }}
                        >
                          <Typography variant="body2" color="text.secondary">
                            No transactions found
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      transactions?.map((transaction, index) => (
                        <TableRow key={transaction?.id || index}>
                          <TableCell sx={tableRowStyles}>
                            {formatDate(transaction.date)}
                          </TableCell>
                          <TableCell sx={tableRowStyles}>
                            <Box
                              sx={{
                                display: "flex",
                                gap: 1,
                                alignItems: "center",
                              }}
                            >
                              {transaction?.transactionDetails ||
                                transaction?.description ||
                                "-"}
                            </Box>
                          </TableCell>
                          <TableCell sx={tableRowStyles}>
                            {PaymentModeFormatter[
                              transaction?.paymentMode as keyof typeof PaymentModeFormatter
                            ] || "-"}
                          </TableCell>
                          <TableCell sx={tableRowStyles}>
                            <Badge
                              badgeContent={transaction?.transactionType}
                              color={getTransactionTypeColor(
                                transaction?.transactionType
                              )}
                              sx={{
                                "& .MuiBadge-badge": {
                                  fontSize: "0.65rem",
                                  height: 16,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  borderRadius: "5px",
                                },
                              }}
                            />
                          </TableCell>
                          <TableCell sx={tableRowStyles}>
                            {formatCurrency(transaction?.amount || 0)}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Pagination Controls */}
              {pagination && pagination.totalPages > 1 && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mt: 2,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Rows per page:
                    </Typography>
                    <FormControl
                      size="small"
                      sx={{ minWidth: 80, fontSize: "0.75rem" }}
                    >
                      <Select
                        value={pageSize}
                        onChange={handlePageSizeChange}
                        displayEmpty
                        size="small"
                        sx={{ fontSize: "0.75rem" }}
                      >
                        <MenuItem value={20} sx={{ fontSize: "0.75rem" }}>
                          20
                        </MenuItem>
                        <MenuItem value={50} sx={{ fontSize: "0.75rem" }}>
                          50
                        </MenuItem>
                        <MenuItem value={100} sx={{ fontSize: "0.75rem" }}>
                          100
                        </MenuItem>
                        <MenuItem value={200} sx={{ fontSize: "0.75rem" }}>
                          200
                        </MenuItem>
                      </Select>
                    </FormControl>
                    <Typography variant="body2" color="text.secondary">
                      {`${
                        (pagination.currentPage - 1) *
                          (pagination.limit || pageSize) +
                        1
                      }-${Math.min(
                        pagination.currentPage * (pagination.limit || pageSize),
                        pagination.totalRecords
                      )} of ${pagination.totalRecords}`}
                    </Typography>
                  </Box>
                  <Pagination
                    count={pagination.totalPages}
                    page={pagination.currentPage}
                    onChange={handlePageChange}
                    color="primary"
                    size="small"
                    showFirstButton
                    showLastButton
                  />
                </Box>
              )}
            </>
          )}
        </Box>
      </Paper>
      <PaymentModal
        open={paymentModalOpen}
        onClose={() => setPaymentModalOpen(false)}
        selectedParty={selectedParty}
        onTransactionAdded={() => {
          fetchTransactions(partyId, partyType, 1);
          setPaymentModalOpen(false);
        }}
        summaryData={summary}
        showTransactions={false}
      />
    </Container>
  );
};

export default Transactions;
